/**
 * General date formatter
 *
 * Configurable using the i18n module
 * configuration key: formatter.date
 * configuration values: {
 *   pattern: The date pattern following a subset of the Java SimpleDateFormat: YYYY means the year, MM means the month
 *   of year and DD means the day of month. We accept any separator between them (usually the hyphen, slash or dot are
 *   in use). Default is YYYY-MM-DD, and the original field content follows this default.
 * }
 */

import i18n from '../../../../shared/modules/i18n';

var localizedTexts = i18n({$markup: undefined, keyPrefix: 'formatter.date'}),

    defaultDateFormatPattern = 'YYYY-MM-DD',
    defaultDateFormatRegex = /^\s*?(\d{0,4})-(\d{0,2})-(\d{0,2})\s*?$/,
    notNumberRegex = /[^\d]/g,
    notFormatRegex = /[^YMD]/g,

    shortKeyMap = {Y: 'year', M: 'month', D: 'day'};

function getDateFormatPattern() {
    return localizedTexts.get('pattern') || defaultDateFormatPattern;
}

function fillWithLeadingZero(value, length) {
    var resultValue = value;
    while (resultValue.length < length) {
        resultValue = '0' + resultValue;
    }
    return resultValue;
}

function asDefaultFormat(normValue) {
    var defaultFormat = [normValue.year, normValue.month, normValue.day].join('-');
    if (defaultFormat === '--') {
        defaultFormat = '';
    }
    return defaultFormat;
}

function extractValue(value) {
    value = value || '';

    var normValue = { year: '', month: '', day: ''},
        cleanedValue,
        dateFormatPattern = getDateFormatPattern(),
        patternText = dateFormatPattern.replace(notFormatRegex, '');

    if (typeof value === 'object' && value instanceof Date) {
        // For Date objects
        normValue.year = value.getFullYear().toString();
        normValue.month = fillWithLeadingZero((value.getMonth() + 1).toString(), 2);
        normValue.day = fillWithLeadingZero(value.getDate().toString(), 2);
        normValue.length = normValue.year.length + normValue.month.length + normValue.day.length;
    } else if (defaultDateFormatRegex.test(value)) {
        // For default pattern values - this is a special case. It is used to convert the original value to the
        // visible one.
        cleanedValue = value.match(defaultDateFormatRegex);
        normValue.year = cleanedValue[1];
        normValue.month = cleanedValue[2];
        normValue.day = cleanedValue[3];
        normValue.length = normValue.year.length + normValue.month.length + normValue.day.length;
    }else {
        // For all other values, value fragments or patterns
        cleanedValue = value.toString().replace(notNumberRegex, '');
        normValue.year = cleanedValue.substring(patternText.indexOf('YYYY'), patternText.indexOf('YYYY') + 4);
        normValue.month = cleanedValue.substring(patternText.indexOf('MM'), patternText.indexOf('MM') + 2);
        normValue.day = cleanedValue.substring(patternText.indexOf('DD'), patternText.indexOf('DD') + 2);
        normValue.length = cleanedValue.length;
    }

    return normValue;
}

/**
 * Format a value according to the provided date pattern.
 * @param value The value to format.
 * @param change Optional. The difference between the current and the previous value to format. This is used to
 *               remove a separator character if there were no change (e.g. push backspace key).
 * @returns An object containing the formatted value, a raw value and a hint.
 */
export default function format(value, change) {
    var normalizedValue = extractValue(value),
        formattedValue = '',
        normItemPos = {
            full: 0,
            year: 0,
            month: 0,
            day: 0
        },
        patternItemPos = 0,
        datePartKey,
        dateFormatPattern = getDateFormatPattern();

    // Generate formatted value and position for hinting
    while (normItemPos.full < normalizedValue.length && patternItemPos < dateFormatPattern.length) {
        if (!notFormatRegex.test(dateFormatPattern[patternItemPos])) {
            datePartKey = shortKeyMap[dateFormatPattern[patternItemPos]];
            formattedValue += normalizedValue[datePartKey][normItemPos[datePartKey]++];
            normItemPos.full++;
        } else {
            formattedValue += dateFormatPattern[patternItemPos];
        }
        patternItemPos++;
    }

    // Append separator if it is the next character
    if ((change === undefined || change.length > 0)
            && patternItemPos < dateFormatPattern.length
            && notFormatRegex.test(dateFormatPattern[patternItemPos])) {
        formattedValue += dateFormatPattern[patternItemPos];
        patternItemPos++;
    }

    return {
        formatted: formattedValue,
        // Set maximum length of the raw
        raw: asDefaultFormat(normalizedValue),
        hint: dateFormatPattern.substring(patternItemPos)
    };
}



//////////////////
// WEBPACK FOOTER
// ./widgets/Common/js/formatter/dateFormatter.js
// module id = 27
// module chunks = main